import React, { useState } from 'react';

import { connect } from 'react-redux';

import type { Moment } from 'moment';

import { userSelector } from '../reducers/current-user-selectors';
import { jobSelector } from '../reducers/job-selectors';
import { orgSelector } from '../reducers/org-selectors';

import { UiButton } from '../ui/ui-button';
import { DatePickerError } from '../ui/ui-date-picker';
import { UiDialog } from '../ui/ui-dialog';

import { exportApplicantData } from './utils/applicant-export-utils';
import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';

export type AdditionalFiltersType = {
  nameOrPhoneNumberFilter?: string;
  locationFilter?: string;
  includeRejectedExpired?: 0 | 1;
};

type InputProps = {
  orgId: number;
  jobId: number | null;
  className?: string;
  // Export filter information
  startDate: Moment | null;
  endDate: Moment | null;
  startError?: DatePickerError | null;
  endError?: DatePickerError | null;
  includeAutoScreenedApplicants: boolean;
  filter?: string;
  additionalFilters?: AdditionalFiltersType;
  chartType?: string;
  disabled?: boolean;
  dataType?: 'eeo' | 'reporting';
  onClose?: () => void;
  setExportError?: (error: string) => void;
};

const mapStateToProps = (state: any, { jobId, orgId }: InputProps) => ({
  org: orgId ? orgSelector(state, orgId) : null,
  job: jobId ? jobSelector(state, jobId) : null,
  user: userSelector(state),
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type Props = InputProps & ReduxProps;

export const ApplicantExport = connect(mapStateToProps)((props: Props) => {
  const {
    jobId,
    orgId,
    className,
    startDate,
    endDate,
    startError,
    endError,
    disabled,
    filter,
    chartType,
    includeAutoScreenedApplicants,
    job,
    user,
    dataType,
    onClose,
    additionalFilters,
    setExportError,
  } = props;

  const id = jobId || orgId;
  let org: Org | null = null;
  if (props.org && orgId) {
    org = props.org;
  } else if (job && jobId) {
    org = job.org;
  }

  if (!id || !org) {
    throw new Error('ApplicantExport requires either orgId or jobId');
  }

  const isJob = Boolean(jobId);

  const [isApplicantExportLoading, setIsApplicantExportLoading] = useState<boolean>(false);
  const [isDownloadingEeoExport, setIsDownloadingEeoExport] = useState<boolean>(false);
  const [isEeoExportLoading, setIsEeoExportLoading] = useState<boolean>(false);
  const showEeoExport =
    (org.usesEqualOpportunityQuestions || org.usesFederalContractorQuestions) &&
    (user.isAccountAdmin || user.isAdmin);

  const handleExport =
    (setLoading: React.Dispatch<React.SetStateAction<boolean>>, type: 'reporting' | 'eeo') =>
    async () => {
      if (!startDate || !endDate || !org) {
        return;
      }

      setLoading(true);

      const { err } = await exportApplicantData({
        org,
        jobId,
        exportType: type,
        startDate,
        endDate,
        filter,
        chartType,
        includeAutoScreenedApplicants,
        additionalFilters,
      });

      if (err && setExportError) {
        setExportError(err);
      }

      setLoading(false);

      if (type === 'eeo') {
        toggleEeoDialog();
      }

      if (onClose && !err) {
        onClose();
      }
    };

  const smartlookExportClick = (eventName: string) => ({
    eventName,
    props: {
      startDate: startDate?.format('MMM DD, YY | hh:mm:ss a') ?? '',
      endDate: endDate?.format('MMM DD, YY | hh:mm:ss a') ?? '',
      days: endDate?.diff(startDate, 'days') ?? 0,
      jobOrAllJobs: isJob ? 'job' : 'all jobs',
    },
  });

  const toggleEeoDialog = () => setIsDownloadingEeoExport(!isDownloadingEeoExport);

  const EeoExportDialog = () => (
    <UiDialog onClose={toggleEeoDialog} size="xs" title="Export EEO Report">
      <UiDialog.Content>
        <div className="typo-title">
          This information is not to be used for applicant evaluation or hiring decisions. It should
          only be used for compliance or assessment of the hiring process as a whole.
        </div>
        <div className="u-spread u-margin-top-lg">
          <UiButton variant="secondary" onClick={toggleEeoDialog}>
            Close
          </UiButton>
          <UiButton
            smartlookEvent={smartlookExportClick('EEO Export Click')}
            onClick={handleExport(setIsEeoExportLoading, 'eeo')}
            loading={isEeoExportLoading}
          >
            Agree and Export
          </UiButton>
        </div>
      </UiDialog.Content>
    </UiDialog>
  );

  const NonEeoExportButton = () => (
    <UiButton
      size="sm"
      className={className || ''}
      smartlookEvent={smartlookExportClick('Applicant Export Click')}
      loading={isApplicantExportLoading}
      disabled={Boolean(startError || endError) || disabled}
      onClick={handleExport(setIsApplicantExportLoading, 'reporting')}
    >
      <MaterialDesignIcon iconName={ICON_NAMES.DOWNLOAD} className="u-margin-right-sm" size="sm" />
      Export Data
    </UiButton>
  );

  const EeoExportButton = () => (
    <>
      <UiButton
        size="sm"
        className={className || ''}
        disabled={
          Boolean(startError || endError) ||
          disabled ||
          isDownloadingEeoExport ||
          !!additionalFilters?.nameOrPhoneNumberFilter
        }
        onClick={toggleEeoDialog}
      >
        <MaterialDesignIcon
          iconName={ICON_NAMES.DOWNLOAD}
          className="u-margin-right-sm"
          size="sm"
        />
        Export EEO Report
      </UiButton>
      {isDownloadingEeoExport && <EeoExportDialog />}
    </>
  );

  if (dataType === 'eeo' && !showEeoExport) {
    return null;
  }

  return dataType === 'eeo' ? <EeoExportButton /> : <NonEeoExportButton />;
});
